import { supabase } from './supabase';
import emailService from './emailService';

const authService = {
  // Sign in with email and password
  signIn: async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        return { 
          success: false, 
          error: 'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.' 
        };
      }
      return { success: false, error: 'Something went wrong during login. Please try again.' };
    }
  },

  // Sign up with email and password
  signUp: async (email, password, userData = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        return { 
          success: false, 
          error: 'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.' 
        };
      }
      return { success: false, error: 'Something went wrong during signup. Please try again.' };
    }
  },

  // Sign out
  signOut: async () => {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Something went wrong during logout. Please try again.' };
    }
  },

  // Get current session
  getSession: async () => {
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to authentication service. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to get session.' };
    }
  },

  // Get user profile
  getUserProfile: async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to load user profile.' };
    }
  },

  // Update user profile
  updateUserProfile: async (userId, updates) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        return { 
          success: false, 
          error: 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' 
        };
      }
      return { success: false, error: 'Failed to update profile.' };
    }
  },

  // Reset password
  resetPassword: async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        return { 
          success: false, 
          error: 'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.' 
        };
      }
      return { success: false, error: 'Failed to send reset email.' };
    }
  },

  // Sign in with OAuth provider
  signInWithOAuth: async (provider) => {
    try {
      // Determine the correct redirect URL based on environment
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const redirectTo = isLocalhost 
        ? `${window.location.origin}/auth/callback`
        : `${window.location.origin}/auth/callback`;

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: redirectTo
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        return { 
          success: false, 
          error: 'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.' 
        };
      }
      return { success: false, error: `Something went wrong during ${provider} login. Please try again.` };
    }
  },

  // Listen for auth state changes
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback);
  },

  // Send welcome email to new user
  sendWelcomeEmail: async (user) => {
    try {
      // Get user profile to get full name
      const profileResult = await authService.getUserProfile(user.id);
      const userProfile = profileResult.success ? profileResult.data : null;

      const userWithProfile = {
        ...user,
        full_name: userProfile?.full_name || user.user_metadata?.full_name || null
      };

      const emailResult = await emailService.sendWelcomeEmail(userWithProfile);
      
      if (!emailResult.success) {
        console.error('Failed to send welcome email:', emailResult.error);
        // Don't fail the registration process if email fails
        return { success: false, error: emailResult.error };
      }

      return { success: true, data: emailResult.data };
    } catch (error) {
      console.error('Error sending welcome email:', error);
      return { success: false, error: error.message };
    }
  },

  // Sign up with welcome email
  signUpWithWelcomeEmail: async (email, password, userData = {}) => {
    try {
      // First, sign up the user
      const signUpResult = await authService.signUp(email, password, userData);

      if (!signUpResult.success) {
        return signUpResult;
      }

      // If signup was successful and user is confirmed, send welcome email
      if (signUpResult.data.user && !signUpResult.data.user.email_confirmed_at) {
        // User needs to confirm email first, welcome email will be sent after confirmation
        console.log('User needs to confirm email before welcome email is sent');
      } else if (signUpResult.data.user) {
        // User is immediately confirmed, send welcome email
        const welcomeResult = await authService.sendWelcomeEmail(signUpResult.data.user);
        if (!welcomeResult.success) {
          console.error('Welcome email failed but signup succeeded:', welcomeResult.error);
        }
      }

      return signUpResult;
    } catch (error) {
      console.error('Error in signUpWithWelcomeEmail:', error);
      return { success: false, error: error.message };
    }
  },

  // ============================================================================
  // MFA/TOTP Methods
  // ============================================================================

  // Check if user is SSO (OAuth) user vs email/password
  isOAuthUser: (user) => {
    if (!user) return false;

    // Check app_metadata for provider
    const provider = user.app_metadata?.provider;

    // If provider is not 'email', it's an OAuth provider (google, discord, twitch, etc.)
    return provider && provider !== 'email';
  },

  // List all MFA factors for the current user
  listMFAFactors: async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to list MFA factors.' };
    }
  },

  // Check if user has TOTP enabled
  hasTOTPEnabled: async () => {
    try {
      const result = await authService.listMFAFactors();

      if (!result.success) {
        return { success: false, error: result.error, enabled: false };
      }

      // Check if there are any verified TOTP factors
      const totpFactors = result.data.totp || [];
      const hasVerifiedTOTP = totpFactors.some(factor => factor.status === 'verified');

      return { success: true, enabled: hasVerifiedTOTP, factors: totpFactors };
    } catch (error) {
      return { success: false, error: 'Failed to check TOTP status.', enabled: false };
    }
  },

  // Enroll in TOTP MFA
  enrollTOTP: async (friendlyName = 'Solis Games') => {
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName,
        issuer: 'Solis Games'
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: {
          id: data.id,
          type: data.type,
          qrCode: data.totp.qr_code, // SVG string
          secret: data.totp.secret,
          uri: data.totp.uri
        }
      };
    } catch (error) {
      return { success: false, error: 'Failed to enroll in TOTP.' };
    }
  },

  // Verify TOTP code during enrollment
  verifyTOTPEnrollment: async (factorId, code) => {
    try {
      // Step 1: Create challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId
      });

      if (challengeError) {
        return { success: false, error: challengeError.message };
      }

      // Step 2: Verify the code
      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: code.trim()
      });

      if (verifyError) {
        return { success: false, error: verifyError.message };
      }

      return { success: true, data: verifyData };
    } catch (error) {
      return { success: false, error: 'Failed to verify TOTP code.' };
    }
  },

  // Challenge and verify TOTP in one call (for login)
  challengeAndVerifyTOTP: async (factorId, code) => {
    try {
      const { data, error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: code.trim()
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to verify TOTP code.' };
    }
  },

  // Unenroll/disable TOTP
  unenrollTOTP: async (factorId) => {
    try {
      const { data, error } = await supabase.auth.mfa.unenroll({
        factorId
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to unenroll TOTP.' };
    }
  },

  // Get authenticator assurance level
  getAuthenticatorAssuranceLevel: async () => {
    try {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      if (error) {
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: {
          currentLevel: data.currentLevel, // 'aal1' or 'aal2'
          nextLevel: data.nextLevel,
          needsMFA: data.nextLevel === 'aal2' && data.currentLevel === 'aal1'
        }
      };
    } catch (error) {
      return { success: false, error: 'Failed to get assurance level.' };
    }
  },

  // ============================================================================
  // Password & Security Management
  // ============================================================================

  // Update user password
  updatePassword: async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: 'Failed to update password.' };
    }
  },

  // Sign out from all sessions
  signOutAllSessions: async () => {
    try {
      // Supabase signOut with scope: 'global' logs out all sessions
      const { error } = await supabase.auth.signOut({ scope: 'global' });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to sign out from all sessions.' };
    }
  },

  // Get current session info
  getCurrentSession: async () => {
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        return { success: false, error: error.message };
      }

      if (!data.session) {
        return { success: false, error: 'No active session' };
      }

      return {
        success: true,
        data: {
          session: data.session,
          user: data.session.user,
          expiresAt: data.session.expires_at,
          createdAt: data.session.user.created_at,
          lastSignInAt: data.session.user.last_sign_in_at
        }
      };
    } catch (error) {
      return { success: false, error: 'Failed to get session info.' };
    }
  }
};

export default authService;
